import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../domain/types';
import { useFavorites } from '../state/FavoritesContext';
import { useAuth } from '../state/auth';
import { useCartActions, useCartState } from '../state/CartContext';
import { useToast, ToastType } from '../state/ToastContext';
import { LazyImage } from './LazyImage';
import { pricingService } from '../domain/services/pricingService';
import { StoreBadge, StoreType } from './ui/StoreBadge';
import { STOCK, UI_ANIMATIONS, DEFAULTS, SHIPPING } from '../domain/constants';

interface ProductCardProps {
    product: Product;
    layout?: 'vertical' | 'horizontal';
    priority?: boolean;
    shopName?: string;
    showOfficialBadge?: boolean;
    itemType?: 'product' | 'service';
    duration?: number; // For services (in minutes)
    serviceLocation?: string; // For services
    storeType?: StoreType; // 'official' | 'verified' | 'basic'
    isNewStore?: boolean;
    isVerified?: boolean;
    hasFastShipping?: boolean;
}

// 1. ISOLATED COMPONENT: Only this part connects to CartState
const QuickActionButton = React.memo(({ 
    product, 
    isLoggedIn, 
    quantityInCart, 
    addToCart, 
    addToast,
    isService
}: { 
    product: Product, 
    isLoggedIn: boolean, 
    quantityInCart: number, 
    addToCart: (p: Product, q: number) => Promise<boolean>,
    addToast: (m: string, t: ToastType) => void,
    isService?: boolean
}) => {
    const handleQuickAdd = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (product.isPro && !isLoggedIn) {
            addToast("Producto exclusivo Club. Ingresa para comprar.", "warning");
            return;
        }

        const success = await addToCart(product, 1);
        if (success) {
            if (navigator.vibrate) navigator.vibrate(UI_ANIMATIONS.VIBRATE_DURATION_MS);
        }
    };

    const handleBookService = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isLoggedIn) {
            addToast("Debes iniciar sesión para reservar", "info");
            return;
        }
        // Navigate to service booking
        window.location.href = `/service/${product.id}/book`;
    };

    // Service booking button - WCAG 2.5.5: min 44x44px touch target
    if (isService) {
        return (
            <button
                onClick={handleBookService}
                className="absolute bottom-2 right-2 h-11 min-w-[44px] px-3 rounded-full shadow-lg flex items-center justify-center gap-1 transition-all z-20 active:scale-90 border border-purple-500 bg-purple-500 text-white hover:bg-purple-600 cursor-pointer"
                aria-label="Reservar servicio"
            >
                <span className="text-[10px] font-bold uppercase tracking-wide">Reservar</span>
                <span className="material-symbols-outlined text-[18px]">calendar_month</span>
            </button>
        );
    }

    if (product.stock === 0) return null;

    if (product.isPro && !isLoggedIn) {
        return (
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToast("Debes iniciar sesión", "info"); }}
                className="absolute bottom-2 right-2 h-11 min-w-[44px] px-3 bg-surface-light/90 dark:bg-black/60 backdrop-blur-md text-text-secondary dark:text-gray-400 rounded-full shadow-lg flex items-center gap-1 justify-center transition-all z-20 border border-black/5 dark:border-white/10 active:scale-95 cursor-pointer"
                aria-label="Iniciar sesión para acceder al Club"
            >
                <span className="material-symbols-outlined text-[18px]">lock</span>
                <span className="text-[10px] font-bold">Club</span>
            </button>
        );
    }

    if (quantityInCart > 0) {
        return (
            <button
                onClick={handleQuickAdd}
                className="absolute bottom-2 right-2 h-11 min-w-[44px] px-3 rounded-full shadow-lg shadow-primary/30 flex items-center justify-center gap-1 transition-all z-20 active:scale-90 border border-primary bg-primary text-white cursor-pointer"
                aria-label={`Agregar al carrito. Cantidad actual: ${quantityInCart}`}
            >
                <span className="material-symbols-outlined text-[18px]">shopping_bag</span>
                <span className="font-black text-xs animate-slide-up key-{quantityInCart}">{quantityInCart}</span>
            </button>
        );
    }

    return (
        <button
            onClick={handleQuickAdd}
            className="absolute bottom-2 right-2 h-11 min-w-[44px] px-3 rounded-full shadow-lg flex items-center justify-center gap-1 transition-all z-20 active:scale-90 border border-black/5 dark:border-white/10 bg-white/95 dark:bg-surface-dark/95 text-text-main dark:text-white hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white group-hover:border-primary/50 cursor-pointer"
            aria-label="Agregar al carrito"
        >
            <span className="text-[10px] font-bold uppercase tracking-wide">Agregar</span>
            <span className="material-symbols-outlined text-[18px]">add</span>
        </button>
    );
});
QuickActionButton.displayName = 'QuickActionButton';

const PriceDisplay = React.memo(({ product, isLoggedIn }: { product: Product, isLoggedIn: boolean }) => {
    const clubPrice = pricingService.calculateClubPrice(product.price || 0);

    if (product.isPro) {
        if (!isLoggedIn) {
            return (
                <div className="flex flex-col">
                    <span className="text-xs text-gray-400 line-through font-medium">$ {(product.originalPrice || product.price || 0).toLocaleString('es-AR')}</span>
                    <div className="flex items-center gap-1 text-primary">
                            <span className="font-bold text-base">$ {(clubPrice || 0).toLocaleString('es-AR')}</span>
                            <span className="material-symbols-outlined text-[14px] filled">lock</span>
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                        <span className="font-bold text-base text-primary">$ {(clubPrice || 0).toLocaleString('es-AR')}</span>
                        <span className="bg-primary/10 text-primary text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">Club</span>
                    </div>
                    {product.originalPrice && <span className="text-[10px] text-gray-400 line-through leading-none">$ {(product.originalPrice || 0).toLocaleString('es-AR')}</span>}
            </div>
        );
    }
    
    return (
        <div className="flex flex-col">
            <div className="flex items-baseline gap-2">
                <p className="text-text-main dark:text-white text-base font-bold leading-normal">$ {(product.price || 0).toLocaleString('es-AR')}</p>
                {product.originalPrice && <span className="text-xs text-gray-400 line-through font-medium">$ {(product.originalPrice || 0).toLocaleString('es-AR')}</span>}
            </div>
            {product.price > SHIPPING.FREE_THRESHOLD && (
                <span className="text-[9px] text-green-600 dark:text-green-400 font-bold flex items-center gap-0.5">
                    <span className="material-symbols-outlined text-[10px]">local_shipping</span> Envío Gratis
                </span>
            )}
        </div>
    );
});
PriceDisplay.displayName = 'PriceDisplay';

interface ProductCardBaseProps extends ProductCardProps {
    isLoggedIn: boolean;
    isSuperAdmin: boolean;
    viewAsUser: boolean;
    isFav: boolean;
    quantityInCart: number;
    toggleFavorite: (id: string) => void;
    addToCart: (p: Product, q: number) => Promise<boolean>;
    addToast: (m: string, t: ToastType) => void;
    itemType: 'product' | 'service';
    duration?: number;
    serviceLocation?: string;
}

const ProductCardBase: React.FC<ProductCardBaseProps> = React.memo(({
    product,
    layout,
    priority,
    shopName,
    showOfficialBadge,
    itemType,
    duration,
    serviceLocation,
  storeType,
  isNewStore,
  isVerified,
  hasFastShipping,
  isLoggedIn,
    isSuperAdmin,
    viewAsUser,
    isFav,
    quantityInCart,
    toggleFavorite,
    addToCart,
    addToast
}) => {
    const isService = itemType === 'service';
    const handleFavClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite(product.id);
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('admin-edit-product', { detail: product }));
    };

    if (layout === 'horizontal') {
        return (
            <div className="flex flex-col gap-3 min-w-[160px] w-[160px] group relative shrink-0" role="article" aria-label={`Product: ${product.title}`}>
                <div className="relative w-full aspect-[4/5] rounded-2xl shadow-sm bg-gray-100 dark:bg-surface-dark overflow-hidden">
                    <Link to={`/product/${product.id}`} className="block w-full h-full" aria-label={`View details of ${product.title}`}>
                        <LazyImage 
                            src={product.coverImageUrl || product.imageUrl || (product.images && product.images[0]) || ''} 
                            alt={product.title}
                            containerClassName="w-full h-full"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            fetchPriority={priority ? "high" : "auto"}
                        />
                    </Link>
                   
                   <div className="absolute top-2 left-2 z-10 flex flex-col gap-1 items-start pointer-events-none">
                       {isService && (
                           <div className="px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-sm bg-purple-500 text-white flex items-center gap-1">
                               <span className="material-symbols-outlined text-[10px]">handshake</span>
                               Servicio
                           </div>
                       )}
                       {showOfficialBadge && (
                           <div className="px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-sm bg-primary text-white flex items-center gap-1">
                               <span className="material-symbols-outlined text-[10px]">verified</span>
                               Oficial
                           </div>
                       )}
                       {product.tags && product.tags.length > 0 ? (
                           product.tags.map((t, idx) => (
                               <div key={idx} className="px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-sm" style={{ backgroundColor: t.bg || '#ffffff', color: t.color || '#000000' }}>
                                   {t.label}
                               </div>
                           ))
                       ) : product.tag ? (
                           <div className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-sm ${product.tagClass || 'bg-white text-black'}`}>
                               {product.tag}
                           </div>
                       ) : null}
                   </div>
                   
                 {isSuperAdmin && !viewAsUser && (
                         <button
                             onClick={handleEditClick}
                             className="absolute top-2 left-2 z-20 min-w-[44px] h-11 bg-black/80 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-primary transition-colors shadow-lg border border-white/20 pointer-events-auto"
                             aria-label="Editar producto"
                         >
                             <span className="material-symbols-outlined text-[18px]">edit</span>
                         </button>
                     )}
                   
                    {!isService && product.stock <= STOCK.LOW_STOCK_THRESHOLD && product.stock > 0 && (
                         <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full z-10 shadow-sm animate-pulse pointer-events-none"></div>
                    )}

                   {!isService && product.stock === 0 && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-black/60 z-20 flex items-center justify-center backdrop-blur-[1px] pointer-events-none">
                            <span className="bg-black text-white px-3 py-1 text-[10px] font-bold rounded-full transform -rotate-12 shadow-xl border border-white/20">AGOTADO</span>
                        </div>
                   )}
                   
                   <QuickActionButton 
                        product={product} 
                        isLoggedIn={isLoggedIn} 
                        quantityInCart={quantityInCart} 
                        addToCart={addToCart}
                        addToast={addToast}
                        isService={isService}
                    />
                   
                     <button
                         onClick={handleFavClick}
                         className="absolute top-2 right-2 min-w-[44px] h-11 bg-white/80 dark:bg-black/60 backdrop-blur-md rounded-full text-text-main dark:text-white opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:scale-110 active:scale-95 cursor-pointer flex items-center justify-center"
                         aria-label={isFav ? `Quitar ${product.title} de favoritos` : `Agregar ${product.title} a favoritos`}
                     >
                         <span className={`material-symbols-outlined text-[20px] ${isFav ? 'filled text-red-500' : ''}`}>favorite</span>
                     </button>
                </div>

               <Link to={`/product/${product.id}`} className="flex flex-col gap-1 mt-1 block">
                   <p className="text-text-main dark:text-white text-xs font-bold leading-tight line-clamp-2 min-h-[32px]">{product.title}</p>
                   <PriceDisplay product={product} isLoggedIn={isLoggedIn} />
                   {isService && duration && (
                       <div className="flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                           <span className="material-symbols-outlined text-[10px]">schedule</span>
                           <span>{duration} minutos</span>
                       </div>
                   )}
               </Link>
           </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 group relative w-full">
            <div className="relative w-full aspect-[4/5] rounded-2xl shadow-sm border border-transparent dark:border-white/5 group-hover:border-primary/20 transition-colors bg-gray-100 dark:bg-surface-dark overflow-hidden">
                <Link to={`/product/${product.id}`} className="block w-full h-full">
                    <LazyImage 
                        src={product.coverImageUrl || product.imageUrl || (product.images && product.images[0]) || ''} 
                        alt={product.title}
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        fetchPriority={priority ? "high" : "auto"}
                    />
                </Link>
                
                <div className="absolute top-2 left-2 z-10 flex flex-col gap-1 items-start pointer-events-none">
                    {isService && (
                        <div className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm bg-purple-500 text-white flex items-center gap-1">
                            <span className="material-symbols-outlined text-[10px]">handshake</span>
                            Servicio
                        </div>
                    )}
                    {product.tags && product.tags.length > 0 ? (
                        product.tags.map((t, idx) => (
                            <div key={idx} className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm" style={{ backgroundColor: t.bg || '#ffffff', color: t.color || '#000000' }}>
                                {t.label}
                            </div>
                        ))
                    ) : product.tag ? (
                        <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm ${product.tagClass || 'bg-white text-black'}`}>
                            {product.tag}
                        </div>
                    ) : null}
                </div>
                
                {isSuperAdmin && !viewAsUser && (
                    <button 
                        onClick={handleEditClick}
                        className="absolute top-2 left-2 z-20 w-8 h-8 bg-black/80 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-primary transition-colors shadow-lg border border-white/20 pointer-events-auto"
                    >
                        <span className="material-symbols-outlined text-[16px]">edit</span>
                    </button>
                )}
                
                {!isService && product.stock <= STOCK.LOW_STOCK_THRESHOLD && product.stock > 0 && (
                    <div className="absolute top-2 left-2 mt-6 px-2 py-1 bg-red-500 text-white text-[9px] font-bold rounded z-10 shadow-sm animate-pulse pointer-events-none">
                        Quedan {product.stock}
                    </div>
                )}

                {!isService && product.stock === 0 && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-black/60 z-20 flex items-center justify-center rounded-2xl backdrop-blur-[1px] pointer-events-none">
                        <span className="bg-black text-white px-3 py-1 text-xs font-bold rounded-full transform -rotate-12 shadow-xl border border-white/20">SIN STOCK</span>
                    </div>
                )}
                
                <QuickActionButton 
                    product={product} 
                    isLoggedIn={isLoggedIn} 
                    quantityInCart={quantityInCart} 
                    addToCart={addToCart}
                    addToast={addToast}
                    isService={isService}
                />

                <div className="absolute bottom-3 left-3 bg-white/90 dark:bg-black/60 backdrop-blur-md text-text-main dark:text-white px-1.5 py-0.5 rounded-lg flex items-center gap-0.5 shadow-sm z-10 group-hover:opacity-0 transition-opacity pointer-events-none">
                     {isService ? (
                        <>
                            <span className="material-symbols-outlined text-[10px] text-purple-500">schedule</span>
                            <span className="text-[10px] font-bold">{duration ? `${duration}m` : 'Servicio'}</span>
                        </>
                     ) : (
                        <>
                            <span className="material-symbols-outlined filled text-[10px] text-yellow-500">star</span>
                            <span className="text-[10px] font-bold">{product.rating || DEFAULTS.PRODUCT_RATING}</span>
                        </>
                     )}
                </div>

                <button 
                    onClick={handleFavClick}
                    className={`absolute top-2 right-2 flex size-8 items-center justify-center rounded-full bg-white/90 dark:bg-black/50 backdrop-blur-md transition-all z-10 hover:scale-110 active:scale-90 shadow-sm cursor-pointer ${isFav ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                >
                    <span className={`material-symbols-outlined text-[18px] ${isFav ? 'filled text-red-500' : 'text-text-secondary dark:text-white hover:text-primary'}`}>favorite</span>
                </button>
            </div>

            <Link to={`/product/${product.id}`} className="flex flex-col block">
                <div className="flex justify-between items-start mb-1">
                    <p className="text-text-main dark:text-white text-sm font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors">{product.title}</p>
                </div>
                <div className="flex flex-col gap-0.5">
                    <PriceDisplay product={product} isLoggedIn={isLoggedIn} />
                    {isService && (
                        <div className="flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                            <span className="material-symbols-outlined text-[10px]">schedule</span>
                            <span>{duration ? `${duration} min` : 'Servicio'}</span>
                        </div>
                    )}
                </div>
                {/* Store Info Section - Shows shop name and badges */}
                <div className="flex flex-col gap-1 mt-1">
                    {shopName && !isService && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-primary truncate">
                                {shopName}
                            </span>
                            {storeType && (
                                <StoreBadge 
                                    type="type" 
                                    storeType={storeType} 
                                    size="sm" 
                                    showIcon={false}
                                />
                            )}
                        </div>
                    )}
                    <p className="text-text-secondary dark:text-gray-400 text-xs font-normal leading-normal truncate">
                        {isService ? (serviceLocation || 'Servicio profesional') : (product.subtitle || product.category)}
                    </p>
                </div>
            </Link>
        </div>
    );
});
ProductCardBase.displayName = 'ProductCardBase';

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  layout = 'vertical', 
  priority = false,
  shopName,
  showOfficialBadge,
  itemType = 'product',
  duration,
  serviceLocation,
  storeType,
  isNewStore,
  isVerified,
  hasFastShipping
}) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user, canAccessAdmin, viewAsUser } = useAuth();
  const { items } = useCartState();
  const { addToCart } = useCartActions();

  const quantityInCart = useMemo(() => {
    const found = items.find(item => item.product.id === product.id);
    return found ? found.quantity : 0;
  }, [items, product.id]);
  const { addToast } = useToast();

  const isFav = isFavorite(product.id);

  return (
    <ProductCardBase
      product={product}
      layout={layout}
      priority={priority}
      shopName={shopName}
      showOfficialBadge={showOfficialBadge}
      itemType={itemType}
      duration={duration}
      serviceLocation={serviceLocation}
      storeType={storeType}
      isNewStore={isNewStore}
      isVerified={isVerified}
      hasFastShipping={hasFastShipping}
      isLoggedIn={!!user}
      isSuperAdmin={canAccessAdmin}
      viewAsUser={viewAsUser}
      isFav={isFav}
      quantityInCart={quantityInCart}
      toggleFavorite={toggleFavorite}
      addToCart={addToCart}
      addToast={addToast}
    />
  );
};
